import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
} from '@material-ui/core';
import { ThemeProvider } from '@material-ui/core/styles';
import { useParams } from 'react-router-dom';
import CustomDialog from '../DiologCustom/index';
import PersonalDataStep from '../PersonalDataStep/index';
import PrescriptionStep from '../PrescriptionStep/index';
import ComplementaryDataStep from '../ComplementaryDataStep/index';
import { useStyles, theme } from './style';
import { GetCep, GetDoctor, GetFollowupPatient, ResgisterPatientPBM, RegisterFollowupPatient, RegisterTermsOneTrust, GetOptin } from '@bforms-web/common';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

export type FormTevaRegisterProps = {
  getDoctor: GetDoctor;
  getFollowupPatient: GetFollowupPatient;
  registerFollowupPatient: RegisterFollowupPatient;
  getCep: GetCep;
  registerTermsOneTrust: RegisterTermsOneTrust;
  getOptin: GetOptin;
  registerPatientPBM: ResgisterPatientPBM;
};

const steps = ['Pré-cadastro', 'Cadastro', 'Dados Complementares'];
const DEFAULT_ERROR_MESSAGE = 'Ocorreu um erro ao salvar os dados. Por favor, tente novamente.';

const MultiStepForm = ({ getDoctor, getFollowupPatient, registerFollowupPatient, getCep, registerTermsOneTrust, getOptin, registerPatientPBM }: FormTevaRegisterProps) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const [openPrescriptionDialog, setOpenPrescriptionDialog] = useState(false);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [showExtraFields, setShowExtraFields] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(DEFAULT_ERROR_MESSAGE);
  const { idfollowup } = useParams<{ idfollowup: string }>();

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      cpf: '',
      birthDate: '',
      email: '',
      name: '',
      gender: '',
      phone: '',
      medicine: 'Ajovy',
      doctorUf: '',
      doctorCrm: '',
      doctorName: '',
      prescription: '',
      presentation: 'Ajovy Solução injetável 225 mg/1,5mL',
      indication: 'Enxaqueca',
      hasStartedTreatment: '',
      doseFrequency: '',
      diagnosticDate: '',
      cep: '',
      state: '',
      city: '',
      neighborhood: '',
      placeDescription: '',
      number: '',
      complement: '',
      occupationalStatus: '',
      initialDiseaseSymptoms: '',
      practicesPhysicalActivity: '',
      physicalActivityType: '',
      previousMedication: '',
      reasonForMigratingToProgramMedication: '',
      otherDiseases: '',
      monthlyHeadacheDays: '',
      acceptProgramTerms: '',
      hasSentValidPrescription: '',
      treatmentOrigin: '',
    },
  });

  const selectedOption = watch('prescription');
  const cepValue = watch('cep');
  const doctorCrmValue = watch('doctorCrm');
  const doctorUfValue = watch('doctorUf');
  const practicesPhysicalActivity = watch('practicesPhysicalActivity');
  const previousMedication = watch('previousMedication');

  const getValidString = (value: any): string | null => {
    return typeof value === 'string' && value.trim() ? value : null;
  };
 
  const extractErrorMessage = (error: any): string => {
  const responseData = error?.response?.data;
  
  const candidates = [
    responseData,
    responseData?.message,
    responseData?.error,
    responseData?.detail,
      Array.isArray(responseData?.errors)
        ? responseData.errors[0]?.message || responseData.errors[0]
        : null,
      error?.message
    ];
 
    return (
      candidates
        .map(getValidString)
        .find(Boolean) || DEFAULT_ERROR_MESSAGE
    );
  };

  const showError = (message?: string) => {
    setErrorMessage(message || DEFAULT_ERROR_MESSAGE);
    setOpenErrorDialog(true);
  };

  useEffect(() => {
    async function fetchStep1DataPatient() {
      if (!idfollowup) return;
      try {
        const followupPatient = await getFollowupPatient.execute(idfollowup);
        const dataPatient = followupPatient.findFollowupPatientAndPatientDataByIdFollowupPatient;
        
        setValue('cpf', dataPatient.patient.cpf);
        setValue('birthDate', dataPatient.patient.birthDate);
        setValue('email', dataPatient.patient.email);
        setValue('name', dataPatient.patient.name);
        setValue('phone', dataPatient.patient.mainPhone);
        setValue(
          'gender',
          dataPatient.patient.gender === "0"
            ? 'MALE'
            : dataPatient.patient.gender === "1"
              ? 'FEMALE'
              : 'NOT_INFORMED'
        );
  
        const formattedDate = new Date(dataPatient.patient.birthDate).toISOString().split('T')[0];
        setValue('birthDate', formattedDate);
  
        if (dataPatient.planPhase === "Cadastro") {
          setActiveStep(1);
        } else {
          setActiveStep(0);
        }
      } catch (error) {
        console.error("Erro ao buscar dados", error);
        setActiveStep(0);
      }
    }
    fetchStep1DataPatient();
  }, [setValue, idfollowup, getFollowupPatient]);

  useEffect(() => {
    if (doctorCrmValue && doctorUfValue) {
      const fetchDoctorName = async () => {
        try {
          const doctor = await getDoctor.execute({ crm: doctorCrmValue, uf: doctorUfValue });
          const response = doctor.getDoctorByCrmUfAndClient;
          if (response && response.name) {
            setValue('doctorName', response.name, { shouldValidate: true, shouldDirty: true });
          }
        } catch (error) {
          console.error('Erro ao buscar dados do médico:', error);
        }
      };
      fetchDoctorName();
    }
  }, [doctorCrmValue, doctorUfValue, setValue, getDoctor]);

  useEffect(() => {
    const fetchCepData = async () => {
      const numericCep = cepValue.replace(/\D/g, '');
      if (numericCep.length === 8) {
        try {
          const data = await getCep.execute(numericCep);
          if (!data.erro) {
            setValue('state', data.uf || '');
            setValue('city', data.localidade || '');
            setValue('neighborhood', data.bairro || '');
            setValue('placeDescription', data.logradouro || '');
            setValue('complement', data.complemento || '');
          }
        } catch (error) { console.error(error); }
      }
    };
    fetchCepData();
  }, [cepValue, setValue, getCep]);

  useEffect(() => {
    if (selectedOption === 'true') {
      setValue('hasSentValidPrescription', 'true');
    } else if (selectedOption === 'false') {
      setValue('hasSentValidPrescription', 'false');
    } else {
      setValue('hasSentValidPrescription', '');
    }
  }, [selectedOption, setValue]);

  useEffect(() => {
    if (previousMedication === 'Sim') {
      setValue('treatmentOrigin', 'Switch');
    } else if (previousMedication === 'Não') {
      setValue('treatmentOrigin', 'Naive');
    }
  }, [previousMedication, setValue]);

  const onSubmit = async (data: any) => {
    if (activeStep === 1) {
      if (data.prescription === 'false') {
        data.presentation = 'Ajovy Solução injetável 225 mg/1,5mL';
        data.indication = 'Enxaqueca';
        const { prescription, ...patientData } = data;
        setIsLoading(true);
        let saved = false;
        try {
          const response = await registerFollowupPatient.execute(patientData);
          if (!response) {
            showError('A API não retornou uma resposta válida ao salvar os dados.');
          } else if (response.message && response.success === false) {
            showError(response.message);
          } else {
            saved = true;
          }
        } catch (error: any) {
          showError(extractErrorMessage(error));
        } finally {
          setIsLoading(false);
        }
        if (!saved) return;
        setOpenPrescriptionDialog(true);
        return;
      } else if (data.prescription === 'true') {
        if (!showExtraFields) {
          setShowExtraFields(true);
          return;
        } else {
          data.presentation = 'Ajovy Solução injetável 225 mg/1,5mL';
          data.indication = 'Enxaqueca';
          const { prescription, ...patientData } = data;
          setIsLoading(true);
          let saved = false;
          try {
            const response = await registerFollowupPatient.execute(patientData);
            if (!response) {
              showError('A API não retornou uma resposta válida ao salvar os dados.');
            } else if (response.message && response.success === false) {
              showError(response.message);
            } else {
              saved = true;
            }
          } catch (error: any) {
            showError(extractErrorMessage(error));
          } finally {
            setIsLoading(false);
          }
          if (!saved) return;
        }
      }
    } else if (activeStep === 2) {
      const { prescription, ...patientData } = data;
      setIsLoading(true);
      let saved = false;
      try {
        const termo = await registerTermsOneTrust.execute(data.cpf);
        const response = await registerFollowupPatient.execute(patientData);
        
        const registerPatientData = mapToRegisterPatientDTO(data);
        const savePatientPBM = await registerPatientPBM.execute(registerPatientData);
        
        const optinPBM = await getOptin.execute({ cpf: data.cpf, programSlug: 'teva_cuidar', canal: '25' });
        
        if (!response || !termo || !optinPBM || !savePatientPBM) {
          showError('Uma ou mais operações não retornaram resposta válida.');
        } else {
          saved = true;
        }
      } catch (error: any) {
        showError(extractErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
      if (!saved) return;
    }

    if (activeStep !== null && activeStep < steps.length) {
      if (activeStep === 0) {
        const { prescription, ...patientData } = data;
        setIsLoading(true);
        let saved = false;
        try {
          const response = await registerFollowupPatient.execute(patientData);
          if (!response) {
            showError('A API não retornou uma resposta válida ao salvar os dados.');
          } else if (response.message && response.success === false) {
            showError(response.message);
          } else {
            saved = true;
          }
        } catch (error: any) {
          showError(extractErrorMessage(error));
        } finally {
          setIsLoading(false);
        }
        if (!saved) return;
      }
      setActiveStep((prev) => (prev !== null ? prev + 1 : 0));
    }
  };

  const getStepContent = (step: number | null) => {
    if (step === null) return null;
    switch (step) {
      case 0:
        return <PersonalDataStep control={control} errors={errors} />;
      case 1:
        return (
          <PrescriptionStep
            control={control}
            errors={errors}
            selectedOption={selectedOption}
            showExtraFields={showExtraFields}
          />
        );
      case 2:
        return <ComplementaryDataStep
          control={control}
          errors={errors}
          practicesPhysicalActivity={practicesPhysicalActivity}
          previousMedication={previousMedication}
        />;
      case 3:
        return (
          <div style={{ textAlign: 'center' }}>
            <h1>Concluído</h1>
            <p>Seu cadastro foi realizado com sucesso.</p>
            <CheckCircleIcon style={{ color: '#4caf50', fontSize: 60 }} />
          </div>
        );
      default:
        return null;
    }
  };

  const handleCloseErrorDialog = () => {
    setOpenErrorDialog(false);
    setErrorMessage(DEFAULT_ERROR_MESSAGE);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className={classes.container}>
        {activeStep !== null && (
          <>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
  
            <CustomDialog
              open={openPrescriptionDialog}
              title="Aviso"
              showCloseButton={false}
              disableBackdropClick
              disableEscapeKeyDown
              description="Você não pode seguir com o cadastro, entre em contato com 0800 para esclarecimentos."
              onClose={() => setOpenPrescriptionDialog(false)}
            />
  
            <CustomDialog
              open={openErrorDialog}
              title="Erro"
              description={errorMessage}
              onClose={handleCloseErrorDialog}
            />
  
            <form onSubmit={handleSubmit(onSubmit)}>
              {getStepContent(activeStep)}
              {activeStep < steps.length && (
                <Box className={classes.buttonRow}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!isValid || isLoading}
                  >
                    {isLoading
                      ? <CircularProgress size={24} color="inherit" />
                      : activeStep === steps.length - 1
                        ? 'Finalizar'
                        : 'Continuar'
                    }
                  </Button>
                </Box>
              )}
            </form>
          </>
        )}
      </Box>
    </ThemeProvider>
  );
};

function mapToRegisterPatientDTO(defaultValues: any) {
  const mapSexo = (value: string): string => {
    if (value === 'MALE') return 'M';
    if (value === 'FEMALE') return 'F';
    return '';
  };

  const data = {
    cpf: defaultValues.cpf,
    originCode: 23,
    paciente: {
      nome: defaultValues.name,
      email: defaultValues.email,
      sexo: mapSexo(defaultValues.gender),
      telefone: defaultValues.phone,
      dataNascimento: defaultValues.birthDate,
      autorizaContato: "S",
    },
    medicamento: {
      ean: '7898587901369',
      crm: defaultValues.doctorCrm,
      uf: defaultValues.doctorUf,
    },
  }

  return data;
}

export default MultiStepForm;